import { useEffect, useMemo, useRef, useState } from "react";
import { CreditCard, Landmark, Loader2, ShieldCheck, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { StripePaymentMethodType } from "@/services/stripe-subs-payments.service";

export interface SavedPaymentMethodOption {
  label: string;
  paymentMethodType: StripePaymentMethodType;
}

type PaymentMethodSelection = StripePaymentMethodType | "saved_method";

interface PaymentMethodSelectionDialogProps {
  open: boolean;
  amount: number;
  currency?: string | null;
  isLoading?: boolean;
  savedMethod?: SavedPaymentMethodOption | null;
  onCancel: () => void;
  onConfirm: (paymentMethodType: StripePaymentMethodType) => Promise<void> | void;
  onConfirmSaved?: () => Promise<void> | void;
}

function formatAmount(amount: number, currency?: string | null) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency ?? "usd").toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
}

const paymentMethodOptions: Array<{
  value: StripePaymentMethodType;
  label: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    value: "us_bank_account",
    label: "Bank account (ACH)",
    description: "Lower estimated fee; ACH payments can take several business days to settle.",
    icon: Landmark,
  },
  {
    value: "card",
    label: "Card",
    description: "Higher estimated fee; card payments usually confirm faster.",
    icon: CreditCard,
  },
];

export default function PaymentMethodSelectionDialog({
  open,
  amount,
  currency,
  isLoading = false,
  savedMethod = null,
  onCancel,
  onConfirm,
  onConfirmSaved,
}: PaymentMethodSelectionDialogProps) {
  const hasSavedMethod = Boolean(savedMethod && onConfirmSaved);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodSelection>("us_bank_account");
  const formattedAmount = useMemo(() => formatAmount(amount, currency), [amount, currency]);
  const savedSelected = selectedMethod === "saved_method";
  const prevOpenRef = useRef(false);

  useEffect(() => {
    // Reset only on the closed -> open transition; if the saved method loads
    // while the dialog is already open it must not override the user's pick.
    if (open && !prevOpenRef.current) {
      setSelectedMethod(hasSavedMethod ? "saved_method" : "us_bank_account");
    } else if (open && !hasSavedMethod && selectedMethod === "saved_method") {
      // The saved method disappeared mid-dialog (e.g. profile was removed);
      // the selection must not point at an option that is no longer rendered.
      setSelectedMethod("us_bank_account");
    }
    prevOpenRef.current = open;
  }, [open, hasSavedMethod, selectedMethod]);

  const handleConfirm = () => {
    if (savedSelected) {
      return onConfirmSaved?.();
    }
    return onConfirm(selectedMethod as StripePaymentMethodType);
  };

  return (
    <Dialog open={open} onOpenChange={(value) => (!value && !isLoading ? onCancel() : undefined)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Select payment method</DialogTitle>
          <DialogDescription>
            The recipient will receive the gross payment minus Shyne&apos;s estimated Stripe processing fee.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Gross payment</span>
            <span className="font-medium">{formattedAmount}</span>
          </div>
        </div>

        <RadioGroup
          value={selectedMethod}
          onValueChange={(value) => setSelectedMethod(value as PaymentMethodSelection)}
        >
          {hasSavedMethod && savedMethod ? (
            <Label
              htmlFor="stripe-payment-method-saved"
              data-testid="payment-method-option-saved"
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors",
                savedSelected ? "border-primary bg-primary/5" : "hover:bg-muted/50"
              )}
            >
              <RadioGroupItem id="stripe-payment-method-saved" value="saved_method" className="mt-1" />
              <ShieldCheck className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <span>
                <span className="block font-medium">Saved payment method</span>
                <span className="mt-1 block text-sm text-muted-foreground">
                  {savedMethod.label} — charged immediately when you confirm.
                </span>
              </span>
            </Label>
          ) : null}
          {paymentMethodOptions.map((option) => {
            const Icon = option.icon;
            const selected = selectedMethod === option.value;
            const optionId = `stripe-payment-method-${option.value}`;

            return (
              <Label
                key={option.value}
                htmlFor={optionId}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors",
                  selected ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                )}
              >
                <RadioGroupItem id={optionId} value={option.value} className="mt-1" />
                <Icon className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <span>
                  <span className="block font-medium">{option.label}</span>
                  <span className="mt-1 block text-sm text-muted-foreground">{option.description}</span>
                </span>
              </Label>
            );
          })}
        </RadioGroup>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="payment-method-confirm"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {savedSelected ? `Pay ${formattedAmount} now` : "Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
